/* eslint-disable @typescript-eslint/no-unused-vars */
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { FilterType, Post, PostWithFiles } from "@/types/mainTypes";
import { userData } from "@/types/mainTypes";
import { setCredentials } from "./slices/authSlice";
import { RootState } from "@/types/storeTypes";

export interface SavedPostsRequest {
  user_id: string;
  page: number;
  pageSize: number;
}
export interface AddPostResponse {
  postId: string;
}
export interface PostResponse {
  hasMore: boolean;
  limit: number;
  page: number;
  posts: Post[];
  totalCount?: number;
}
export interface GetPostDet {
  post_id: string;
  user_id: string | null;
}
interface SavePostResponse {
  message: string;
  savedPost?: { user_id: string; post_id: string };
  unsavedPost?: { user_id: string; post_id: string };
}
export const api = createApi({
  reducerPath: "api",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.NEXT_PUBLIC_API_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ["Posts", "SavedPosts"],
  endpoints: (build) => ({
    // ─── AUTH ─────────────────────────────────────────────────────────────────
    verifyUser: build.mutation<
      { user: userData; token: string; isValid: boolean },
      { phone: string; otp: string }
    >({
      query: (body) => ({ url: "auth/verfiy-phone", method: "POST", body }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        dispatch(
          setCredentials({
            user: data.user,
            token: data.token,
            isValid: data.isValid,
          })
        );
      },
    }),
    sendOTP: build.mutation<
      { message: string; isSend: boolean },
      { phone: string }
    >({
      query: (body) => ({ url: "auth/send-otp", method: "POST", body }),
    }),
    loginUser: build.mutation<
      { isValid: boolean },
      { user_phone: string }
      // { user_phone: string; user_password: string }
    >({
      query: (body) => ({ url: "auth/login", method: "POST", body }),
    }),

    registerUser: build.mutation<{ isValid: boolean }, { user: userData }>({
      query: ({ user }) => ({
        url: "auth/register",
        method: "POST",
        body: user,
      }),
    }),

    isActive: build.query<{ is_active: boolean }, void>({
      query: () => ({ url: "is-active", method: "GET" }),
    }),

    // ─── POSTS ─────────────────────────────────────────────────────────────────

    getSaved: build.query<PostResponse, SavedPostsRequest>({
      query: ({ user_id, page, pageSize }) => ({
        url: `/saved?page=${page}&limit=${pageSize}`,
        method: "POST",
        body: { user_id },
      }),
      providesTags: (result, error, { user_id }) => {
        if (error || !result) {
          return [{ type: "Posts" as const, id: "LIST" }];
        }
        return [
          { type: "SavedPosts", id: `LIST-${user_id}` },
          ...(result?.posts || []).map((post) => ({
            type: "Posts" as const,
            id: post.id,
          })),
        ];
      },
    }),

    getPosts: build.query<
      PostResponse,
      {
        filter: FilterType;
        page?: number;
        pageSize?: number;
        user_id?: string | null;
        category_id?: number | null;
        marks_only?: boolean;
        request?: "listed" | "saved" | null;
      }
    >({
      query: ({
        filter,
        page,
        pageSize = 20,
        user_id = null,
        marks_only = false,
        request = null,
      }) => {
        const body = {
          ...filter,
          page,
          pageSize,
          user_id,
          marks_only,
          request,
        };

        return {
          url: `filter?page=${page}&limit=${pageSize}`,
          method: "POST",
          body,
        };
      },

      transformResponse: (rawJson: unknown): PostResponse => {
        const response = rawJson as PostResponse;
        console.log("this is the response", response.posts);
        return response;
      },
      providesTags: (result, error, arg) => {
        if (error || !result) {
          return [{ type: "Posts" as const, id: "LIST" }];
        }
        return [
          ...result.posts.map(({ id }) => ({ type: "Posts" as const, id })),
          { type: "Posts" as const, id: "LIST" },
        ];
      },
    }),

    addPost: build.mutation<AddPostResponse, FormData>({
      query: (newPost) => ({
        url: "create-post",
        method: "POST",
        body: newPost,
      }),
      invalidatesTags: [{ type: "Posts", id: "LIST" }],
    }),
    incrementViewCount: build.mutation<
      { newViewsValue: number },
      { post_id: string }
    >({
      query: ({ post_id }) => ({
        url: `increment-view`,
        method: "PATCH",
        body: { post_id },
      }),
    }),
    getPostDetails: build.query<Post, GetPostDet>({
      query: ({ post_id, user_id }) => ({
        url: `post-details?post_id=${post_id}&user_id=${user_id}`,
        method: "POST",
      }),
      providesTags: (result, error, { post_id }) => {
        if (error || !result) {
          return [];
        }
        return [{ type: "Posts", id: post_id }];
      },
    }),
    savePost: build.mutation<SavePostResponse, GetPostDet>({
      query: ({ post_id, user_id }) => ({
        url: `save-post?post_id=${post_id}&user_id=${user_id}`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { post_id, user_id }) => [
        { type: "Posts", id: post_id },
        { type: "SavedPosts", id: `LIST-${user_id}` },
      ],
    }),
  }),
});

export const {
  useVerifyUserMutation,
  useLoginUserMutation,
  useSendOTPMutation,
  useRegisterUserMutation,
  useIsActiveQuery,
  useGetPostsQuery,
  useLazyGetPostsQuery,
  useAddPostMutation,
  useGetPostDetailsQuery,
  useSavePostMutation,
  useGetSavedQuery,
  useIncrementViewCountMutation,
  // useUpdatePostMutation,
  // useDeletePostMutation,
} = api;
